import { Get_Last_Runes_GamesQuery, Get_Last_Runes_Games_By_PlayerQuery } from '@/__generated__/graphql'
import { GET_RUNES_GAMES, GET_RUNES_GAMES_BY_PLAYER } from '@/api/query'
import { LastGamesList } from '@/components/organisms/LastGamesList/LastGamesList'
import { Flex, SimpleGrid, Text } from '@chakra-ui/react'
import { useQuery } from '@apollo/client'
import { memo, useState } from 'react'
import { AppModals, useAppModal } from '@/context/ModalProvider'
import { useTranslation } from 'next-i18next/pages'
import { useSession } from '@/context/SessionProvider'

export const LastGames = memo(() => {
  const [onlyMine, setOnlyMine] = useState(false)
  const modal = useAppModal()
  const { t } = useTranslation()
  const { account } = useSession()
  const address = account?.injectiveAddress

  const allQuery = useQuery(GET_RUNES_GAMES, {
    pollInterval: 30_000,
    returnPartialData: true,
    fetchPolicy: 'no-cache',
    skip: onlyMine,
  })

  const myQuery = useQuery(GET_RUNES_GAMES_BY_PLAYER, {
    pollInterval: 30_000,
    returnPartialData: true,
    fetchPolicy: 'no-cache',
    variables: { player: address || '' },
    skip: !onlyMine || !address,
  })

  const activeGames = onlyMine ? transformRuneGamesByPlayer(myQuery.data) : transformRuneGames(allQuery.data).recent
  const activeLoading = onlyMine ? myQuery.loading : allQuery.loading
  const activeError = onlyMine ? !!myQuery.error : !!allQuery.error

  return (
    <Flex h={'full'} p={{ base: 4, sm: 5 }} bg={'ninja.600'} rounded={'2xl'} flexDir={'column'}>
      <SimpleGrid
        flexShrink={0}
        gap={1}
        p={1}
        h={10}
        fontSize={'xs'}
        fontWeight={700}
        bg={'ninja.500'}
        rounded={'xl'}
        columns={address ? 2 : 1}
        mb={3}
      >
        <Flex
          onClick={() => setOnlyMine(false)}
          cursor={'pointer'}
          rounded={'lg'}
          bg={!onlyMine ? '#3D3C70' : 'transparent'}
          justifyContent={'center'}
          alignItems={'center'}
        >
          <Text color={!onlyMine ? 'white' : 'ninja.300'}>{t('common.last-games')}</Text>
        </Flex>
        {address && (
          <Flex
            onClick={() => setOnlyMine(true)}
            bg={onlyMine ? '#3D3C70' : 'transparent'}
            cursor={'pointer'}
            rounded={'lg'}
            justifyContent={'center'}
            alignItems={'center'}
          >
            <Text color={onlyMine ? 'white' : 'ninja.300'}>{t('common.my-games')}</Text>
          </Flex>
        )}
      </SimpleGrid>
      <LastGamesList
        gameDetailsCallback={(id) => modal.openModalNew(AppModals.RunesGameDetails, { gameId: id })}
        error={activeError}
        loading={activeLoading}
        games={activeGames as any}
        viewAllHref={onlyMine && address ? `/activity/${address}?game=runes` : undefined}
      />
    </Flex>
  )
})

const mapRuneGame = (game: any) => ({
  id: (game?.id || '') as string,
  player: game?.player || '',
  payout: { amount: game?.payout || '', denom: game?.denom || '' },
  bet: { amount: game?.bet || '', denom: game?.denom || '' },
  status: game?.payout > game?.bet ? 'WIN' : 'LOSE',
  betUsd: game?.betUsd != null ? String(game.betUsd) : undefined,
  payoutUsd: game?.payoutUsd != null ? String(game.payoutUsd) : undefined,
})

const transformRuneGames = (data?: Get_Last_Runes_GamesQuery) => {
  return {
    recent: data?.runesGames?.nodes.map(mapRuneGame).reverse() || [],
  }
}

const transformRuneGamesByPlayer = (data?: Get_Last_Runes_Games_By_PlayerQuery) => {
  return data?.runesGames?.nodes.map(mapRuneGame).reverse() || []
}
